import { useState, type ReactNode } from "react";
import { ChevronDown, LocateFixed, X } from "lucide-react";
import { useConfirm } from "@/lib/confirm";
import { cn } from "@/lib/cn";
import { IconeColorido } from "./IconeColorido";
import { iconeDaCategoria } from "./icones-da-selecao";

export type ItemEscolhido = { id: string; nome: string; categoria: string; proprio?: boolean };

/** Até aqui o resumo abre tudo; acima, só os nomes das categorias (dá pra abrir cada uma). */
const ABRE_TUDO_ATE = 12;

/**
 * O que já foi escolhido, por categoria: abre e fecha cada grupo, tira um item ou a categoria
 * inteira e leva até ela na lista. Leva também a ação do passo (Próximo, Concluir).
 */
export function ResumoDaSelecao({
  itens,
  unidade,
  onRemover,
  onIrPara,
  acao,
  className,
}: {
  itens: ItemEscolhido[];
  /** "serviços" / "procedimentos" */
  unidade: string;
  onRemover: (itens: ItemEscolhido[]) => void;
  onIrPara?: (categoria: string) => void;
  acao?: ReactNode;
  className?: string;
}) {
  const confirm = useConfirm();
  const categorias = [...new Set(itens.map((i) => i.categoria))];
  const [abertas, setAbertas] = useState<Set<string>>(new Set());
  const [fechadas, setFechadas] = useState<Set<string>>(new Set());
  const abreTudo = itens.length <= ABRE_TUDO_ATE;
  const aberta = (c: string) => (abreTudo ? !fechadas.has(c) : abertas.has(c));
  const alternar = (c: string) => {
    const mudar = (s: Set<string>) => {
      const n = new Set(s);
      if (n.has(c)) n.delete(c);
      else n.add(c);
      return n;
    };
    if (abreTudo) setFechadas(mudar);
    else setAbertas(mudar);
  };

  const limpar = async () => {
    const ok = await confirm({ title: `Tirar todos os ${itens.length} ${unidade}?`, confirmText: "Tirar todos", cancelText: "Cancelar", destructive: true });
    if (ok) onRemover(itens);
  };

  return (
    <aside className={cn("flex flex-col rounded-xl border border-zinc-200 bg-white shadow-sm dark:border-border dark:bg-card", className)}>
      <div className="flex items-start justify-between gap-2 border-b border-zinc-100 px-4 py-3 dark:border-border">
        <div>
          <p className="text-2xl font-bold leading-none tabular-nums text-zinc-900 dark:text-foreground">{itens.length}</p>
          <p className="mt-1 text-xs text-zinc-500 dark:text-muted-foreground">
            {unidade} em {categorias.length} {categorias.length === 1 ? "categoria" : "categorias"}
          </p>
        </div>
        {itens.length > 0 && (
          <button type="button" onClick={limpar} className="text-[11.5px] font-semibold text-zinc-500 hover:text-red-600 dark:text-muted-foreground">
            Limpar tudo
          </button>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-2 py-2">
        {itens.length === 0 && <p className="px-2 py-8 text-center text-xs text-zinc-400 dark:text-muted-foreground">Nada escolhido ainda.</p>}
        {categorias.map((c) => {
          const doGrupo = itens.filter((i) => i.categoria === c);
          const abre = aberta(c);
          return (
            <section key={c} className="group/grupo">
              <div className="flex items-center gap-1 rounded-lg pr-1 hover:bg-zinc-50 dark:hover:bg-muted/50">
                <button type="button" onClick={() => alternar(c)} aria-expanded={abre} className="flex min-w-0 flex-1 items-center gap-2 px-1.5 py-1.5 text-left">
                  <IconeColorido {...iconeDaCategoria(c)} tamanho="sm" />
                  <span title={c} className="line-clamp-2 min-w-0 flex-1 text-[12.5px] font-semibold leading-tight text-zinc-800 dark:text-foreground">{c}</span>
                  <span className="text-[11px] tabular-nums text-zinc-500 dark:text-muted-foreground">{doGrupo.length}</span>
                  <ChevronDown className={cn("h-3.5 w-3.5 shrink-0 text-zinc-400 transition-transform", abre && "rotate-180")} />
                </button>
                {onIrPara && (
                  <button type="button" onClick={() => onIrPara(c)} title="Mostrar na lista" aria-label={`Mostrar ${c} na lista`} className="rounded p-1 text-zinc-400 hover:bg-zinc-200 hover:text-zinc-700 dark:hover:bg-border">
                    <LocateFixed className="h-3.5 w-3.5" />
                  </button>
                )}
                <button type="button" onClick={() => onRemover(doGrupo)} title="Tirar a categoria" aria-label={`Tirar ${c}`} className="rounded p-1 text-zinc-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-500/10">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
              {abre && (
                <ul className="mb-1 ml-9 border-l border-zinc-100 pl-2 dark:border-border">
                  {doGrupo.map((s) => (
                    <li key={s.id} className="group flex items-center justify-between gap-2 rounded px-1.5 py-1 text-[12.5px] text-zinc-700 hover:bg-zinc-50 dark:text-foreground/90 dark:hover:bg-muted">
                      <span className="min-w-0 truncate">
                        {s.nome}
                        {s.proprio && <span className="ml-1.5 text-[10px] font-semibold uppercase text-brand-600 dark:text-primary">próprio</span>}
                      </span>
                      <button type="button" onClick={() => onRemover([s])} aria-label={`Tirar ${s.nome}`} className="shrink-0 rounded p-0.5 text-zinc-400 hover:text-zinc-700">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          );
        })}
      </div>

      {acao && <div className="border-t border-zinc-100 p-3 dark:border-border">{acao}</div>}
    </aside>
  );
}
